import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, SlidersHorizontal, Download, ExternalLink, Loader, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { RootState } from '../store/reducers';
import { fetchDecisionsRequest } from '../store/decisions/actions';
import { exportCSV } from '../store/decisions/service';
import './Decisions.css';

const statutLabels: Record<string, { label: string; color: string; bg: string }> = {
  brouillon:  { label: 'Brouillon',   color: '#6B7280', bg: '#F3F4F6' },
  a_verifier: { label: 'À vérifier',  color: '#C4A535', bg: '#FFF8E7' },
  valide:     { label: 'Validé',       color: '#047857', bg: '#ECFDF5' },
  anonymise:  { label: 'Anonymisé',    color: '#2563EB', bg: '#EFF6FF' },
  publie:     { label: 'Publié',       color: '#059669', bg: '#D1FAE5' },
};

const statutOptions = ['', 'brouillon', 'a_verifier', 'valide', 'anonymise', 'publie'];

const PAGE_SIZE = 20;

const Decisions: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { decisions, isLoading, count, next, previous } = useSelector(
    (state: RootState) => state.decisions
  );

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatut, setSelectedStatut] = useState('');
  const [selectedJuridiction, setSelectedJuridiction] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const fetchPage = useCallback((page: number, search?: string, statut?: string, juridiction?: string) => {
    const params: any = { page, page_size: PAGE_SIZE };
    if (search) params.search = search;
    if (statut) params.statut = statut;
    if (juridiction) params.juridiction = juridiction;
    dispatch(fetchDecisionsRequest(params));
  }, [dispatch]);

  // Initial load
  useEffect(() => {
    fetchPage(1);
  }, [fetchPage]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      fetchPage(1, value, selectedStatut, selectedJuridiction);
    }, 400);
    setSearchTimeout(timeout);
  };

  const handleStatutChange = (value: string) => {
    setSelectedStatut(value);
    setCurrentPage(1);
    fetchPage(1, searchQuery, value, selectedJuridiction);
  };

  const handleJuridictionChange = (value: string) => {
    setSelectedJuridiction(value);
    setCurrentPage(1);
    fetchPage(1, searchQuery, selectedStatut, value);
  };

  const handlePageChange = (direction: 'next' | 'prev') => {
    if (direction === 'next' && next) {
      // Utiliser directement l'URL next
      const url = new URL(next);
      const params: any = {};
      url.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      dispatch(fetchDecisionsRequest(params));
      setCurrentPage(currentPage + 1);
    } else if (direction === 'prev' && previous) {
      // Utiliser directement l'URL previous
      const url = new URL(previous);
      const params: any = {};
      url.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      dispatch(fetchDecisionsRequest(params));
      setCurrentPage(currentPage - 1);
    }
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const params: any = {};
      if (searchQuery) params.search = searchQuery;
      if (selectedStatut) params.statut = selectedStatut;
      if (selectedJuridiction) params.juridiction = selectedJuridiction;
      const response = await exportCSV(params);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `decisions_export_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé.');
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.detail) {
        toast.error(data.detail);
      } else {
        toast.error("Erreur lors de l'export CSV.");
      }
    } finally {
      setIsExporting(false);
    }
  };

  const totalPages = Math.ceil(count / PAGE_SIZE);

  const getAgentName = (agent: any) => {
    if (!agent) return '—';
    if (agent.first_name || agent.last_name) {
      return `${agent.first_name || ''} ${agent.last_name || ''}`.trim();
    }
    return agent.email || '—';
  };

  const getParties = (d: any) => {
    const parts = [];
    if (d.demandeur) parts.push(d.demandeur);
    if (d.defendeur) parts.push(d.defendeur);
    return parts.join(' c/ ') || '—';
  };

  return (
    <div className="decisions-page">
      {/* Filters bar */}
      <div className="decisions-filters">
        <div className="decisions-search">
          <Search size={16} className="decisions-search-icon" />
          <input
            type="text"
            placeholder="Rechercher par numéro, parties, nature..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        <div className="decisions-filter-group">
          <select
            value={selectedStatut}
            onChange={(e) => handleStatutChange(e.target.value)}
            className="decisions-select"
          >
            <option value="">Tous les statuts</option>
            {statutOptions.filter(Boolean).map(s => (
              <option key={s} value={s}>
                {statutLabels[s]?.label || s}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Juridiction..."
            value={selectedJuridiction}
            onChange={(e) => handleJuridictionChange(e.target.value)}
            className="decisions-select"
            style={{ minWidth: 160 }}
          />

          <button className="btn-filter">
            <SlidersHorizontal size={14} /> Avancé
          </button>
        </div>
      </div>

      {/* Results header */}
      <div className="decisions-results-header">
        <span className="decisions-count">
          {isLoading ? 'Chargement...' : `${count} résultat${count > 1 ? 's' : ''}`}
        </span>
        <button className="btn-export" onClick={handleExportCSV} disabled={isExporting}>
          <Download size={14} /> {isExporting ? 'Export en cours...' : 'Exporter CSV'}
        </button>
      </div>

      {/* Table */}
      <div className="decisions-table-wrapper">
        {isLoading && decisions.length === 0 ? (
          <div className="decisions-loading">
            <Loader size={28} className="decisions-spinner" />
            <span>Chargement des décisions...</span>
          </div>
        ) : (
          <>
            <table className="decisions-table">
              <thead>
                <tr>
                  <th>Date traitement</th>
                  <th>N° Décision</th>
                  <th>Juridiction</th>
                  <th>Date décision</th>
                  <th>Statut</th>
                  <th>Agent</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {decisions.filter(d => d.statut !== 'brouillon' && d.date_decision).map(d => {
                  const s = statutLabels[d.statut] || { label: d.statut, color: '#666', bg: '#f3f4f6' };
                  return (
                    <tr key={d.id}>
                      <td className="td-date-traitement">
                        {d.created_at
                          ? (() => {
                              const dt = new Date(d.created_at);
                              const date = dt.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                              const time = dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
                              return `${date} à ${time}`;
                            })()
                          : '—'}
                      </td>
                      <td className="td-numero">{d.numero || '—'}</td>
                      <td>{d.juridiction || '—'}</td>
                      <td>{d.date_decision ? new Date(d.date_decision).toLocaleDateString('fr-FR') : '—'}</td>
                      <td>
                        <span className="status-badge" style={{ color: s.color, background: s.bg }}>
                          {s.label}
                        </span>
                      </td>
                      <td>{getAgentName(d.agent)}</td>
                      <td>
                        <button
                          className="btn-open"
                          onClick={() => navigate(`/espace-tca/decisions/${d.id}`)}
                        >
                          <ExternalLink size={14} /> Ouvrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {decisions.length === 0 && !isLoading && (
              <div className="decisions-empty">
                Aucune décision ne correspond aux critères de recherche.
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="decisions-pagination">
          <button
            className="decisions-page-btn"
            disabled={!previous}
            onClick={() => handlePageChange('prev')}
          >
            <ChevronLeft size={16} /> Précédent
          </button>
          <span className="decisions-page-info">
            Page {currentPage} sur {totalPages}
          </span>
          <button
            className="decisions-page-btn"
            disabled={!next}
            onClick={() => handlePageChange('next')}
          >
            Suivant <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Decisions;
